"use client";

import { useEffect, useRef } from "react";
import { gridMetrics } from "@/components/hero/HeroGrid";

export type RowBandVariant = "blue" | "magenta" | "neutral" | "dark" | "pink";

/** Opaque band surface colors — clearing blocks inside an opaque band must use
 *  the same value via `data-grid-fill` so rounded corners don't flash light. */
export const DARK_BAND = "#14141c";
export const PINK_BAND = "#d70e62";

const SURFACES: Record<RowBandVariant, string> = {
  // Translucent washes: the body grid stays visible through them.
  blue: "rgba(47,107,224,0.065)",
  magenta: "rgba(215,14,98,0.055)",
  neutral: "rgba(16,16,28,0.05)",
  // Opaque "drenched" surfaces that repaint the grid in light hairlines.
  dark: DARK_BAND,
  pink: PINK_BAND,
};

/** Grid-line alpha for the opaque variants (light lines on the surface). */
const OPAQUE_LINES: Partial<Record<RowBandVariant, number>> = {
  dark: 0.075,
  pink: 0.14,
};

/**
 * Full-bleed background band whose top and bottom edges snap to grid row
 * lines (whole rows only — never a band edge mid-cell). Light variants are
 * translucent washes over the body grid; `dark` is opaque and repaints the
 * same grid geometry in light hairlines (aligned: its top sits on a row line
 * and its x-offset uses --grid-offset-x). Desktop-only, like the grid itself.
 */
export default function RowBand({ variant }: { variant: RowBandVariant }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const compute = () => {
      const w = document.documentElement.clientWidth;
      if (w < 768) {
        el.style.display = "none";
        return;
      }
      const parent = el.parentElement;
      if (!parent) return;
      el.style.display = "";
      const { cell } = gridMetrics(w);
      const rect = parent.getBoundingClientRect();
      const topDoc = rect.top + window.scrollY;
      const bottomDoc = topDoc + rect.height;
      // First full row line at/after the section top, last at/before its end.
      // EPS: sections engineered to start/end exactly ON a row line (band-to-
      // band butts, whole-row tails plus anti-collapse paddings) measure
      // within ~2px of it — two flush `md:pt-px`/`md:pb-px` sections meeting
      // contribute both their 1px paddings — snapping must not lose or gain a
      // whole row over that. Matches useBandRowAlign/GridClearing (2.5). A
      // band may poke ≤2px past its section; sections don't clip.
      const EPS = 2.5;
      const top = Math.ceil((topDoc - EPS) / cell) * cell - topDoc;
      const bottom = bottomDoc - Math.floor((bottomDoc + EPS) / cell) * cell;
      el.style.top = `${top}px`;
      el.style.bottom = `${bottom}px`;
      // Tell the navbar an opaque surface (possibly at the very top of the
      // page) has taken its position — its own mount-time check can run
      // before this effect. NOT grid-relayout: RowBand listens to that
      // itself, dispatching it here would loop.
      if (lines) window.dispatchEvent(new Event("nav-surface-change"));
    };

    compute();
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready.then(compute).catch(() => {});

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    window.addEventListener("resize", schedule);
    window.addEventListener("grid-relayout", schedule);
    return () => {
      window.removeEventListener("resize", schedule);
      window.removeEventListener("grid-relayout", schedule);
      cancelAnimationFrame(raf);
    };
  }, [variant]);

  const lines = OPAQUE_LINES[variant];
  return (
    <div
      ref={ref}
      aria-hidden
      // Opaque drenched surfaces announce themselves so the fixed navbar can
      // flip to its light-on-dark theme while one passes underneath it.
      data-nav-surface={lines ? variant : undefined}
      className="pointer-events-none absolute inset-x-0"
      style={{
        background: SURFACES[variant],
        ...(lines
          ? {
              backgroundImage: `linear-gradient(to right, rgba(255,255,255,${lines}) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,${lines}) 1px, transparent 1px)`,
              backgroundSize: "var(--grid-cell, 72px) var(--grid-cell, 72px)",
              backgroundPosition: "var(--grid-offset-x, 0px) 0",
            }
          : undefined),
      }}
    />
  );
}
